import React, { useEffect, useMemo, useState } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { userApi, User, UserRQ, UserUpdateRQ, UserRole } from '../../utils/userApi';
import { segmentApi, Segment } from '../../utils/segmentApi';
import { getRoleDisplayName, isWhiteLabelRole } from '../../utils/roleMapping';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon, Delete as DeleteIcon, Edit as EditIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import toast from 'react-hot-toast';
import DataTable, { Column } from './DataTable';
import { formatPhoneNumber, cleanPhoneNumber, phoneMaskProps } from '../../utils/phoneMask';
import { useTranslation } from 'react-i18next';

const baseRoles: UserRole[] = ['Admin', 'Manager', 'Sales'];

function UsersList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user: currentUser } = useAuth();
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [filtered, setFiltered] = useState<User[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState<string>('name');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const rs = await userApi.list();
      setUsers(rs);
      setFiltered(rs);
    } catch (e: any) {
      toast.error(e?.message || t('users.errors.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const txt = searchText.trim().toLowerCase();
    if (!txt) { setFiltered(users); return; }
    setFiltered(
      users.filter(u =>
        u.name.toLowerCase().includes(txt) ||
        u.username.toLowerCase().includes(txt) ||
        u.phone.toLowerCase().includes(txt)
      )
    );
  }, [searchText, users]);

  const sorted = React.useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = (a as any)[orderBy] ?? '';
      const bv = (b as any)[orderBy] ?? '';
      if (typeof av === 'string' && typeof bv === 'string') {
        return order === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return 0;
    });
    return arr;
  }, [filtered, orderBy, order]);

  const paged = React.useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('users.removeConfirm'))) return;
    try {
      await userApi.remove(id);
      toast.success(t('users.notifications.removed'));
      await load();
    } catch (e: any) {
      toast.error(e?.message || t('users.errors.removeError'));
    }
  };

  const columns: Column<User>[] = [
    { id: 'name', label: t('users.name'), minWidth: 160 },
    { id: 'username', label: t('users.username'), minWidth: 120 },
    { id: 'phone', label: t('users.phone'), minWidth: 120 },
    { id: 'role', label: t('users.profile'), minWidth: 100, format: (value) => getRoleDisplayName(value) },
    {
      id: 'actions',
      label: t('users.actions'),
      minWidth: 100,
      format: (_v, row) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-center' }}>
          <Tooltip title={t('users.editTooltip')}>
            <IconButton
              size="small"
              onClick={() => {
                if (isWhiteLabelRole(row.role) && !isWhiteLabelRole(currentUser?.role || '')) {
                  toast.error(t('users.noPermissionToEdit'));
                  return;
                }
                navigate(`${row.id}/edit`);
              }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={isWhiteLabelRole(row.role) ? t('users.whiteLabelCannotDelete') : t('users.deleteTooltip')}>
            <IconButton
              size="small"
              onClick={() => {
                if (isWhiteLabelRole(row.role)) {
                  toast.error(t('users.whiteLabelCannotDelete'));
                  return;
                }
                handleDelete(row.id);
              }}
              color="error"
              disabled={isWhiteLabelRole(row.role)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{t('users.manage')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
          onClick={() => navigate('create')}>{t('users.newUser')}
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          placeholder={t('users.searchPlaceholder')}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {filtered.length} {filtered.length === 1 ? t('users.userFound') : t('users.usersFound')}
        </Typography>
      </Paper>

      <DataTable<User>
        columns={columns}
        rows={paged}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        total={sorted.length}
        orderBy={orderBy}
        order={order}
        onPageChange={(p) => setPage(p)}
        onRowsPerPageChange={(r) => { setRowsPerPage(r); setPage(0); }}
        onRequestSort={(col) => {
          if (orderBy === col) {
            setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
          } else {
            setOrderBy(col);
            setOrder('asc');
          }
        }}
      />
    </Box>
  );
}

function UserForm({ isEdit }: { isEdit?: boolean }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const params = useParams();
  const userId = params.userId as string | undefined;
  const { user: currentUser } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!!isEdit);
  const [managers, setManagers] = useState<Array<Pick<User, 'id' | 'name' | 'username'>>>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [form, setForm] = useState<UserRQ>({
    name: '',
    username: '',
    phone: '',
    role: 'Sales',
    managerUserId: undefined,
    active: true,
    shouldResetPassword: false,
    password: '',
    segmentId: undefined
  });

  const availableRoles = useMemo(() => {
    const roles = [...baseRoles];
    
    if (isEdit && form.role === 'WhiteLabel') {
      roles.push('WhiteLabel');
    }
    
    return roles;
  }, [isEdit, form.role]);

  const isProfileDisabled = useMemo(() => {
    // Only disable if we're editing a user and that user has WhiteLabel role
    return isEdit && form.role === 'WhiteLabel';
  }, [isEdit, form.role]);

  useEffect(() => {
    const load = async () => {
      if (!isEdit || !userId) { setInitialLoading(false); return; }
      try {
        const rs = await userApi.get(userId);
        setForm({
          name: rs.name,
          username: rs.username,
          phone: rs.phone || '',
          role: rs.role as UserRole, // Keep backend role (English) for form
          managerUserId: rs.managerUserId ?? undefined,
          active: rs.active,
          shouldResetPassword: rs.shouldResetPassword,
          segmentId: rs.segmentId ?? undefined,
        });
      } catch (e: any) {
        toast.error(e?.message || t('users.errors.loadUserError'));
      } finally {
        setInitialLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, userId]);

  useEffect(() => {
    const loadManagers = async () => {
      try {
        const rs = await userApi.getManagers();
        setManagers(rs);
      } catch (e) {
        // ignore silently for now
      }
    };
    loadManagers();
  }, []);

  useEffect(() => {
    const loadSegments = async () => {
      try {
        const rs = await segmentApi.list();
        setSegments(rs);
      } catch (e) {
        // ignore silently
      }
    };
    loadSegments();
  }, []);

  const canSubmit = useMemo(() => {
    if (isEdit) {
      return form.name && form.phone && form.role;
    }
    return form.name && form.username && form.phone && form.role;
  }, [form, isEdit]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (isEdit && userId) {
        const payload: UserUpdateRQ = {
          name: form.name,
          phone: form.phone,
          role: form.role,
          managerUserId: form.managerUserId,
          active: form.active,
          shouldResetPassword: form.shouldResetPassword,
          segmentId: form.segmentId,
        };
        if (form.password) {
          payload.password = form.password;
        }
        const result = await userApi.update(userId, payload);
        if (result.success) {
          toast.success(t('users.notifications.updated'));
          navigate('..');
        } else {
          toast.error(result.error || t('users.errors.updateError'));
        }
      } else {
        const result = await userApi.create(form); // form.role is already in English
        if (result.success) {
          toast.success(t('users.notifications.created'));
          navigate('..');
        } else {
          toast.error(result.error || t('users.errors.createError'));
        }
      }
    } catch (e: any) {
      // Fallback para erros não esperados
      toast.error(t('users.errors.unexpectedError'));
      console.error('Erro não tratado:', e);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate('..')}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{isEdit ? t('users.edit') : t('users.newUser')}</Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }
        }}
      >
        <Box>
          <TextField fullWidth label={t('users.name')} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
        </Box>
        <Box>
          <TextField fullWidth label={t('users.username')} value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} disabled={isEdit} />
        </Box>
        <Box>
          <TextField
            fullWidth
            label={t('users.phone')}
            value={formatPhoneNumber(form.phone)}
            onChange={e => {
              const cleaned = cleanPhoneNumber(e.target.value);
              setForm({ ...form, phone: cleaned });
            }}
            {...phoneMaskProps}
          />
        </Box>
        <Box>
          <TextField 
            select 
            fullWidth 
            label={t('users.profile')} 
            value={form.role} 
            onChange={e => setForm({ ...form, role: e.target.value as UserRole })}
            disabled={isProfileDisabled}
            helperText={isProfileDisabled ? t('users.profileCannotChange') : ""}
          >
            {availableRoles.map(r => <MenuItem key={r} value={r}>{getRoleDisplayName(r)}</MenuItem>)}
          </TextField>
        </Box>
        <Box>
          <TextField select fullWidth label={t('users.manager')} value={form.managerUserId ?? ''} onChange={e => setForm({ ...form, managerUserId: (e.target.value || undefined) as any })}>
            <MenuItem value="">{t('users.none')}</MenuItem>
            {managers.map(m => (
              <MenuItem key={m.id} value={m.id}>{m.name} ({m.username})</MenuItem>
            ))}
          </TextField>
        </Box>
        <Box>
          <TextField select fullWidth label={t('users.segment')} value={form.segmentId ?? ''} onChange={e => setForm({ ...form, segmentId: (e.target.value || undefined) as any })}>
            <MenuItem value="">{t('users.none')}</MenuItem>
            {segments.map(s => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
          </TextField>
        </Box>
        <Box>
          <TextField type="password" fullWidth label={t('users.password')} value={form.password || ''} onChange={e => setForm({ ...form, password: e.target.value })} placeholder={isEdit ? t('users.passwordPlaceholder') : ''} />
        </Box>
        <Box sx={{ gridColumn: '1 / -1' }}>
          <FormControlLabel control={<Switch checked={form.active} onChange={(_, v) => setForm({ ...form, active: v })} />} label={t('users.active')} />
          <FormControlLabel control={<Switch checked={form.shouldResetPassword} onChange={(_, v) => setForm({ ...form, shouldResetPassword: v })} />} label={t('users.forcePasswordReset')} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
        <Button onClick={() => navigate('..')}>{t('common.cancel')}</Button>
        <Button variant="contained" sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }} disabled={!canSubmit || loading} onClick={handleSubmit}>{loading ? t('users.saving') : t('users.save')}</Button>
      </Box>
    </Paper>
  );
}

const UsersManagement: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<UsersList />} />
      <Route path="/create" element={<UserForm />} />
      <Route path=":userId/edit" element={<UserForm isEdit />} />
    </Routes>
  );
};

export default UsersManagement;


